import type { Draft } from 'immer';
import type { TRunWithPayload, TUseResponse } from 'pullstate';
import type { useEffect } from 'react';
import { startTransition, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAsync } from 'react-streaming';

import { useEffectOnce } from '@/renderer/utils/hooks/useEffectOnce';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import type { Method, RequestSchema } from '@/types/request';

import { PullstateCore } from '../store';
import { request } from './api';
import { injectIntoInfiniteData2 } from './api/infinite';
import { IS_SERVER, useLayoutEffectOrEffect } from './ssr';
import { useAuthToken } from './useMe';
import useOnlineStatus from './useOnlineStatus';
import { usePageContext } from './usePageContext';
import useWebSocket, { WS_BASE_URL } from './useWebSocket2';
import type { Subscription } from './ws/types';

export interface UseRequestOpts<T, Q> {
  /** If false (default), the request is blocking and runs immediately upon mount */
  manual?: boolean;
  /** If false (default), the request will run on server as well. If also not `manual`, it is a blocking SSR request. */
  clientOnly?: boolean;
  // @todo somehow cast this better
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatResult?: (data: any) => T;
  /** HTTP method */
  method?: Method;
  /** URL query string parameters */
  query?: Q;
  /** If false (default), shows error/warning alert popups */
  hideAlert?: boolean;
  /** If false (default), attempts to refresh session token preemptively before your request when necessary */
  skipRefresh?: boolean;
  /** If false (default), tries to use `jsonResponse.data` as `data` if available */
  storeRaw?: boolean;
}

type WithNew<Ty extends API.DbItem> = Ty & {
  isNew?: boolean;
};

interface UseInfiniteRequestOpts<
  T extends API.DbItem,
  D,
  Q,
  M,
  FromSocketType,
  InitialInput extends RequestInfo = RequestInfo,
> {
  /** Defaults to "id"; "createdAt" is another option */
  cursorKey?: keyof T;
  /** Event name to use for WebSocket updates */
  event?: string;
  /** Return false to short-circuit, return new data to override json, or return nothing to keep json same */
  eventValidate?: (
    event: MessageEvent,
    json:
      | API.SubscriptionResponse<WithNew<T>, string, FromSocketType>
      | API.WebsocketResult<API.Id>,
    knownIds: React.MutableRefObject<Set<number>>,
  ) =>
    | boolean
    | undefined
    | void
    | API.SubscriptionResponse<T>
    | Promise<boolean | undefined | void | API.SubscriptionResponse<T>>;
  /** Used in SSR */
  fetch?: typeof fetch;
  /** Unique key for this request set; only when this changes, will the request re-run from scratch */
  key: string;
  /** Request schema */
  req: RequestSchema<API.WithPage<T[], M>, D, InitialInput, Q> | string;
  /** Query to run for the initial data set; if not set, uses `query` */
  initialQuery?: Q;
  /** If true, stops the initial request from running at all */
  pause?: boolean;
  /** Change polling delays */
  PollingDelays?: Record<keyof typeof PollingDelaysDefault, number>;
  /** Query to run on subsequent fetches */
  query?: Q;
  /** If false (default), the initial request will run on client only */
  serverSkip?: boolean;
  /** When true, we don't use the fallback polling mechanism */
  skipPoll?: boolean;
  readonly __type?: T;
  readonly __dtype?: D;
}

interface UseInfiniteRequestResult<T, M> {
  data: API.WithPage<T[], M> | null | undefined;
  error: Error | null | undefined;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  isLoading: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  isRefetching: boolean;
  isSuccess: boolean;
  refetch: () => void;
  renderPayload: TRunWithPayload<API.WithPage<T[], M>>;
  reset: () => void;
  wsConnected: boolean;
  newestItem: T | undefined;
  oldestItem: T | undefined;
  isEmpty: boolean;
}

const LIMIT = 50;
const POLL_LIMIT = 10;
const _BASE_PARAM = { cursor: 0 as number | undefined, limit: LIMIT };

export enum PollingDelaysDefault {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 2000,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 180000,
  // Out-of-business hours
  WS_CONNECTED_MAX_OOB = 300000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 3000,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 31000,
  // Out-of-business hours
  MAX_OOB = 60000,
  INCREMENT = 500,
  ERROR_INCREMENT = 5000,
}

const InfiniteRequest = PullstateCore.createAsyncActionDirect(
  async <
    const ItemType extends API.DbItem,
    const BodyType = void,
    const QueryType extends API.PageInput = API.PageInput,
    const MetaType = void,
    const FromSocketType = ItemType,
  >([opts, knownIds]: [
    UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
    Set<API.Id>,
  ]) => {
    const { req, initialQuery, query, pause } = opts;

    if (pause) {
      return null;
    }

    const dir = (initialQuery?.order || query?.order)?.dir || 'DESC';
    const pageParam = (initialQuery || query)?.page;
    const pageDirection = dir === 'DESC' ? ('after' as const) : ('before' as const);
    const cursor = pageParam?.[pageDirection];

    const initialOpts = {
      query: {
        ...(initialQuery || query),
        order: {
          by: 'id',
          dir: 'DESC',
          ...(initialQuery?.order || query?.order),
        },
        page: {
          limit: pageParam?.limit || LIMIT,
          ...(initialQuery?.page || query?.page),
          [pageDirection]: cursor,
        },
      },
    } as UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>;

    const reqo: RequestSchema<
      API.WithPage<ItemType[], MetaType>,
      BodyType,
      RequestInfo,
      QueryType
    > = typeof req === 'string' ? { input: req } : req;

    const json: API.WithPage<ItemType[], MetaType> = await request(
      {
        // ...restOpts,
        ...reqo,
        init: {
          ...reqo.init,
          signal: reqo.init?.signal,
        },
      },
      {
        // body: strProps,
        noStringify: true,
        query: initialOpts.query,
        // hideAlert: opts?.hideAlert,
        // skipRefresh: opts?.skipRefresh,
        fetch: IS_SERVER ? opts.fetch : undefined, // SSR fetch includes headers from page request
      },
    );

    knownIds.clear();
    for (const item of json.data || []) {
      knownIds.add(item.id);
    }

    return json;
  },
  {
    actionId: '∞',
    subsetKey: <
      const ItemType extends API.DbItem,
      const BodyType = void,
      const QueryType extends API.PageInput = API.PageInput,
      const MetaType = void,
      const FromSocketType = ItemType,
    >(
      opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
    ) => {
      return [opts.key, JSON.stringify(opts.initialQuery || opts.query), JSON.stringify(opts.req)];
    },
  },
);

/** In SSR, we use useAsync, which throws the promise until it is finished */
const useAsyncOrLayoutEffect: <T>(
  a: Parameters<typeof useEffect>[1],
  b: () => T,
) => Awaited<T> | void = IS_SERVER
  ? useAsync
  : (a: Parameters<typeof useEffect>[1], b: Parameters<typeof useAsync>[1]) => {
      useLayoutEffect(() => {
        b();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, a);
    };

function isNewDbItem<T = unknown>(item: T): item is WithNew<API.DbItem<T>> {
  return (item as API.DbItemBase).id ? true : false;
}

const POLLING_RUNNING_INITIAL: number | null = null;

// Between these hours, use the extra long MAX pause
const BUSINESS_HOURS_START = 5;
const BUSINESS_HOURS_END = 23;

const ptTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric',
  hour12: false,
  hourCycle: 'h23',
});

const JITTER = 500;

export function useInfiniteRequest<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.PageInput = API.PageInput,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
): UseInfiniteRequestResult<ItemType, MetaType> {
  const {
    req,
    event: eventName,
    eventValidate,
    cursorKey,
    initialQuery: _initialQuery, // @todo should probably use this
    key,
    serverSkip,
    skipPoll,
    query,
    pause,
    PollingDelays = PollingDelaysDefault,
  } = opts;
  const pageContext = usePageContext();

  // Initial request (via Pullstate Hook)

  const [_isMountedState, setIsMountedState] = useState(false);
  const isMounted = useRef(false);
  const keyRef = useRef(key);
  const knownIds = useRef<Set<API.Id>>(new Set());
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const result = InfiniteRequest.use(
    [IS_SERVER ? { ...opts, fetch: pageContext.fetch } : opts, knownIds.current],
    {
      key,
      ssr: true,
      initiate: IS_SERVER && serverSkip ? false : !pause,
      dormant: !isMounted.current,
      // cacheBreak: !isMounted.current || keyRef.current !== key,
      holdPrevious: true,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as unknown as TUseResponse<any, API.WithPage<ItemType[], void>, string>;

  const queryRef = useRef(query);

  if (queryRef.current !== query) {
    queryRef.current = query;
  }

  if (keyRef.current !== key) {
    keyRef.current = key;
    queryRef.current = query;
  }

  const execute = useRef(result.execute);

  if (execute.current !== result.execute) {
    execute.current = result.execute;
  }

  const updateCached = useRef(result.updateCached);

  if (updateCached.current !== result.updateCached) {
    updateCached.current = result.updateCached;
  }

  useUpdateEffect(() => {
    // result.clearCached();
  }, [key, typeof req === 'string' ? req : req.input]);

  // Wait for Pullstate hook to resolve

  useAsyncOrLayoutEffect([key], async () => {
    if (IS_SERVER) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      await PullstateCore.useInstance().resolveAsyncState();
    }
  });

  // Pagination info

  const dir = query?.order?.dir || 'DESC';
  const pageParam = query?.page;
  const pageDirection = dir === 'DESC' ? ('after' as const) : ('before' as const);
  const cursor = pageParam?.[pageDirection];

  // Next Page
  const cursorVal =
    dir === 'DESC' ? result.payload?.pageInfo.endCursor : result.payload?.pageInfo.startCursor;
  const nextPage = useRef({
    cursor: cursorVal,
    running: false,
    has: !!result.payload?.pageInfo.hasNextPage,
  });

  if (nextPage.current.cursor !== cursorVal) {
    nextPage.current.cursor = cursorVal;
  }

  if (nextPage.current.has !== !!result.payload?.pageInfo.hasNextPage) {
    nextPage.current.has = !!result.payload?.pageInfo.hasNextPage;
  }

  const reqRef = useRef(req);

  if (req !== reqRef.current) {
    reqRef.current = req;
  }

  const fetchNextPage = useCallback(async () => {
    if (nextPage.current.running || !nextPage.current.has) {
      return;
    }

    nextPage.current.running = true;
    const curKey = keyRef.current;

    try {
      setIsFetchingNextPage(true);

      const pageCursorKey = dir === 'DESC' ? 'endCursor' : 'startCursor';

      const initialOpts = {
        query: {
          ...query,
          order: {
            by: 'id',
            dir: 'DESC',
            ...query?.order,
          },
          page: {
            limit: pageParam?.limit || LIMIT,
            ...query?.page,
            [pageDirection]: nextPage.current.cursor || cursor,
          },
        },
      } as UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>;

      const reqo: RequestSchema<
        API.WithPage<ItemType[], MetaType>,
        BodyType,
        RequestInfo,
        QueryType
      > = typeof reqRef.current === 'string' ? { input: reqRef.current } : reqRef.current;

      const json: API.WithPage<ItemType[], MetaType> = await request(
        {
          ...reqo,
          init: {
            ...reqo.init,
            signal: reqo.init?.signal,
          },
        },
        {
          noStringify: true,
          query: initialOpts.query,
          fetch: IS_SERVER ? opts.fetch : undefined,
        },
      );

      nextPage.current.cursor = json.pageInfo[pageCursorKey];

      const hasData = json.data?.length;

      if (hasData || json.pageInfo.hasNextPage !== result.payload?.pageInfo.hasNextPage) {
        startTransition(() => {
          updateCached.current(
            (draft: Draft<API.WithPage<API.DbItemBase[], void>>, original) => {
              if (hasData) {
                injectIntoInfiniteData2(
                  json.data,
                  original,
                  draft as API.WithPage<ItemType[], void>,
                  knownIds,
                  cursorKeyRef.current,
                );
              }

              const nextPageChanged = json.pageInfo.hasNextPage !== draft.pageInfo.hasNextPage;
              const totalChanged =
                json.pageInfo.total && json.pageInfo.total !== draft.pageInfo.total;
              const cursorChanged =
                json.pageInfo[pageCursorKey] &&
                json.pageInfo[pageCursorKey] !== draft.pageInfo[pageCursorKey];

              if (nextPageChanged) {
                draft.pageInfo.hasNextPage = json.pageInfo.hasNextPage;
              }

              if (totalChanged) {
                draft.pageInfo.total = json.pageInfo.total;
              }

              if (cursorChanged) {
                draft.pageInfo[pageCursorKey] = json.pageInfo[pageCursorKey];
              }

              nextPage.current.has = !!json.pageInfo.hasNextPage;

              // draft.meta = json.meta || draft.meta;
            },
            { resetTimeCached: true, key: curKey, notify: isMounted.current },
          );
        });
      }

      nextPage.current.running = false;
      setIsFetchingNextPage(false);
    } catch (error) {
      nextPage.current.running = false;
      setIsFetchingNextPage(false);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @todo Prev Page

  const fetchPreviousPage = useCallback(() => {
    console.warn('not yet implemented');
  }, []);

  const refetch = useCallback(async () => {
    const response = await execute.current({
      key: keyRef.current,
    });

    return response;
  }, []);

  const reset = useCallback(() => {
    console.warn('not yet implemented');
  }, []);

  //
  // Websocket shenanigans
  //

  const authToken = useAuthToken();
  const isOnline = useOnlineStatus();
  const pollingDelay = useRef(PollingDelays.WS_CONNECTED_START);

  // Event validator fn
  const eventValidateRef = useRef(eventValidate);

  if (eventValidate !== eventValidateRef.current) {
    eventValidateRef.current = eventValidate;
  }

  if (IS_SERVER && result.payload?.data.length) {
    for (const item of result.payload.data) {
      knownIds.current.add(item.id);
    }
  }

  // Cursor key
  const cursorKeyRef = useRef<keyof ItemType>(cursorKey || 'id');

  if ((cursorKey || 'id') !== cursorKeyRef.current) {
    cursorKeyRef.current = cursorKey || 'id';
  }

  // Message changed updater
  const onInfiniteUpdated = useMemo(
    () => {
      return async (event: MessageEvent, ojson: API.WebsocketResult<API.Id>) => {
        const curKey = keyRef.current;
        const rjson = (await eventValidateRef.current?.(event, ojson, knownIds)) as
          | false
          | API.SubscriptionResponse<ItemType>;

        if (rjson === false) {
          return;
        }

        if (typeof rjson === 'number') {
          // Still a number, fetch from DB
          console.warn('Received update from WS but eventValidate did not fetch the data');

          return;
        }

        const json = rjson || ojson;

        const newItem = json.data;
        const isInSet = knownIds.current.has(newItem.id);

        if (isInSet) {
          // We have this in our cache somewhere. Update it.
          startTransition(() => {
            updateCached.current(
              (oldData: Draft<API.WithPage<API.DbItemBase[], void>>) => {
                for (const entryIndex in oldData.data) {
                  const entry = oldData.data[entryIndex];

                  if (entry.id === newItem.id) {
                    // Same item, replace it
                    oldData.data.splice(+entryIndex, 1, {
                      ...entry,
                      ...newItem,
                    });
                  }
                }
              },
              { resetTimeCached: true, key: curKey, notify: isMounted.current },
            );
          });
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Message received injector
  const onInfiniteCreated = useCallback<Subscription<WithNew<ItemType>, string, FromSocketType>>(
    async (event, ojson) => {
      if (isNewDbItem(ojson.data)) {
        ojson.data.isNew = true;
      }

      const curKey = keyRef.current;
      const res = await eventValidateRef.current?.(event, ojson, knownIds);

      if (res === false) {
        return;
      }

      const json = (res as API.SubscriptionResponse<ItemType>) || ojson;

      // startTransition(() => {
      updateCached.current(
        (draft, original) => {
          injectIntoInfiniteData2(
            [json.data as ItemType],
            original,
            draft as API.WithPage<ItemType[], void>,
            knownIds,
            cursorKeyRef.current,
          );
        },
        { resetTimeCached: true, key: curKey, notify: isMounted.current },
      );
      // });

      loop(); // temp fix: force re-fetch instantly
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const ws = useWebSocket(WS_BASE_URL, {
    connect: !!eventName && !!authToken,
    subscriptions: eventName
      ? {
          [`${eventName}/updated`]: onInfiniteUpdated,
          [`${eventName}/created`]: onInfiniteCreated,
        }
      : undefined,
  });

  const wsReady = useRef(ws.ready);

  const PollingDelaysRef = useRef(PollingDelays);

  if (PollingDelaysRef.current !== PollingDelays) {
    PollingDelaysRef.current = PollingDelays;
  }

  useLayoutEffectOrEffect(() => {
    if (ws.ready) {
      if (pollingDelay.current < PollingDelaysRef.current.WS_CONNECTED_START) {
        pollingDelay.current = PollingDelaysRef.current.WS_CONNECTED_START;
      }
    } else {
      pollingDelay.current = PollingDelaysRef.current.START;
    }

    wsReady.current = ws.ready;
  }, [ws.ready, isOnline, PollingDelays]);

  //
  // End of websocket shenanigans
  //

  //
  // Polling thingamajig
  //

  const isPollingRunning = useRef(POLLING_RUNNING_INITIAL);
  const pollingCursor = useRef<API.Id>(0);
  const pollingTimer = useRef<NodeJS.Timeout>();

  // We should periodically poll for new messages. Though less often when WS is connected; it's just a backup.
  const loop = useCallback(async () => {
    if (isPollingRunning.current || !isMounted.current || skipPoll) {
      // Prevent having this run multiple times
      return;
    }

    const curKey = keyRef.current;
    const pollingId = Math.random();

    isPollingRunning.current = pollingId;

    const currentHour = +ptTime.format(new Date());
    const isOob = currentHour < BUSINESS_HOURS_START || currentHour > BUSINESS_HOURS_END;

    try {
      const cursorCurrent = pollingCursor.current;

      const res = await request<API.WithPage<WithNew<ItemType>[], MetaType>, BodyType, QueryType>(
        reqRef.current,
        {
          query: {
            ...query,
            order: {
              by: 'id',
              dir: cursorCurrent ? 'ASC' : 'DESC',
            },
            page: {
              [cursorCurrent ? 'after' : 'before']: cursorCurrent === 0 ? undefined : cursorCurrent,
              limit: POLL_LIMIT,
            },
          },
        } as unknown as QueryType,
      );

      if (isPollingRunning.current !== pollingId || !isMounted.current) {
        return; // short circuit in case something went weird
      }

      if (res.data.length) {
        // We found new data! Inject it in
        const newData: WithNew<ItemType>[] = [];

        // Update cursorCurrent
        const pageCursor = res.pageInfo[cursorCurrent ? 'endCursor' : 'startCursor'];
        const nextCursor = pageCursor ? pageCursor : res.data[0].id || cursorCurrent;

        if (nextCursor > cursorCurrent) {
          pollingCursor.current = nextCursor;
        }

        for (const item of res.data) {
          // Avoid duplicates
          if (!knownIds.current.has(item.id)) {
            knownIds.current.add(item.id);
            newData[cursorCurrent ? 'unshift' : 'push'](item);
          }
        }

        if (newData.length) {
          res.data = newData;

          startTransition(() => {
            updateCached.current(
              (draft: Draft<API.WithPage<API.DbItemBase[], void>>, original) => {
                injectIntoInfiniteData2(
                  newData,
                  original,
                  draft as API.WithPage<ItemType[], void>,
                  knownIds,
                  cursorKeyRef.current,
                  POLL_LIMIT,
                );
              },
              { resetTimeCached: true, key: curKey, notify: isMounted.current },
            );
          });

          if (wsReady.current) {
            pollingDelay.current = PollingDelaysRef.current.WS_CONNECTED_JUST_RECEIVED;
          } else {
            pollingDelay.current = PollingDelaysRef.current.JUST_RECEIVED;
          }
        }
      } else {
        // Data is empty; that means we're up to date, so use the latest cursorCurrent from websocket
        const nextCursor =
          result.payload?.data.length &&
          result.payload.pageInfo[cursorCurrent ? 'endCursor' : 'startCursor'];

        if (nextCursor && nextCursor > cursorCurrent) {
          pollingCursor.current = nextCursor;
        }

        if (wsReady.current) {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
            isOob
              ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
              : PollingDelaysRef.current.WS_CONNECTED_MAX,
          );
        } else {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.INCREMENT,
            PollingDelaysRef.current.MAX,
            isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
          );
        }
      }
    } catch (error) {
      console.warn(error);

      if (wsReady.current) {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
          isOob
            ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
            : PollingDelaysRef.current.WS_CONNECTED_MAX,
        );
      } else {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.ERROR_INCREMENT,
          isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
        );
      }
    }

    clearTimeout(pollingTimer.current); // just in case another one started up
    isPollingRunning.current = null;

    if (isMounted.current) {
      // Only keep running if still mounted
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffectOrEffect(() => {
    if (!IS_SERVER) {
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );

      return () => {
        clearTimeout(pollingTimer.current);
      };
    }
  }, [loop, PollingDelays]);

  //
  // End of polling
  //

  useLayoutEffectOrEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useAsyncOrLayoutEffect([key], async () => {
    if (!IS_SERVER) {
      const response = await execute.current({
        key,
      });

      nextPage.current.has = !!response.payload?.pageInfo.hasNextPage;
    }
  });

  useEffectOnce(() => {
    setIsMountedState(true);
  });

  return {
    error: result.errorPayload,
    hasNextPage: !!result.payload?.pageInfo?.hasNextPage,
    hasPreviousPage: false, // result.data?.pageInfo.hasPreviousPage
    isError: !result.isFailure,
    isFetching: result.isLoading || result.isUpdating,
    isFetchingNextPage,
    isFetchingPreviousPage: false,
    isLoading: result.isLoading,
    isPending: !result.isFinished,
    isPlaceholderData: false,
    isRefetching: false,
    isSuccess: result.isSuccess,
    renderPayload: result.renderPayload as TRunWithPayload<API.WithPage<ItemType[], MetaType>>,
    data: result.payload as typeof result.payload extends null
      ? null
      : API.WithPage<ItemType[], MetaType>,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
    reset,
    newestItem: result.payload?.data?.[0],
    oldestItem: result.payload?.data?.[result.payload?.data.length - 1],
    isEmpty:
      !result.payload?.data?.[0] && !result.payload?.data?.[result.payload?.data?.length - 1],
    wsConnected: ws.ready,
  };
}
